"use client"

//import { Badge } from "@/components/ui/badge"
//import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { avatarHex, stageTint, TINT } from "@/lib/stage-colors"
//import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
//import { getCandidateById } from "@/lib/mock-data"
import { useGetJobsJobIdCandidate } from "openapi/api/endpoints"
import { Spinner } from "./ui/spinner"

export interface PipelineCandidateCardProps {
  jobId: string
  candidateId: any
  onClick: (candidate: any) => void
}

/* const getSourceIcon = (source: string) => {
  switch (source) {
    case "LinkedIn":
      return <Linkedin className="h-3 w-3 text-blue-600" />
    case "AI Search":
      return <Star className="h-3 w-3 text-purple-600" />
    case "Referral":
      return <MessageSquare className="h-3 w-3 text-green-600" />
    case "Direct Apply":
      return <Mail className="h-3 w-3 text-orange-600" />
    case "Headhunter":
      return <Phone className="h-3 w-3 text-red-600" />
    default:
      return <Star className="h-3 w-3 text-gray-600" />
  }
} */

export function PipelineCandidateCard({ jobId, candidateId, onClick }: PipelineCandidateCardProps) {
  //const candidate = getCandidateById(candidateId)
  const { data, isLoading, error } = useGetJobsJobIdCandidate(jobId, {candidate_id: candidateId});

  if (isLoading) {
    return (
      <Card className="opacity-50">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground"><Spinner></Spinner></p>
        </CardContent>
      </Card>
    )
  }

  if (!data||error) {
    return (
      <Card className="opacity-50">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">Candidate not found</p>
        </CardContent>
      </Card>
    )
  }

  const responseData = data as any
  // Legacy nested payload support intentionally disabled.
  // const raw = responseData?.raw_json ?? responseData?.candidates?.raw_json ?? {}
  const raw = responseData?.raw_json ?? {}
  const experiences = responseData?.experiences?.length ? responseData.experiences : raw?.experiences ?? []
  const candidate = {
    ...responseData,
    full_name: responseData?.full_name ?? raw?.full_name,
    headline: responseData?.headline ?? raw?.headline,
    profile_pic_url: raw?.profile_pic_url,
    experiences,
  }

  const initials = candidate.full_name
    ? candidate.full_name
        .split(" ")
        .map((part: string) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?"

  /*
    Canvas 4b: one line of identity, one of context, nothing else. The board is
    for seeing where everyone is, not for reading about them — detail is a click
    away in the profile. Initials are tinted from a fixed palette so a column
    scans as distinct people rather than a wall of identical chips.
  */
  // The same six the candidates list uses, and a token rather than a literal:
  // the artboard ramp is drawn against near-black and is unreadable as the
  // colour of initials on a white card.
  const tint = avatarHex(candidate.full_name ?? "?")

  /*
    A div, deliberately, not a button. This card is the drag content inside a
    `Draggable`, and a nested interactive element swallows the pointer events the
    drag sensor needs — making candidates unmovable. Keyboard access comes from
    the drag handle props the Draggable already spreads onto this element's
    parent, plus the explicit Enter/Space handler below.
  */
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(candidate)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onClick(candidate)
        }
      }}
      className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg border bg-card px-3 py-[11px] text-left transition-colors hover:border-foreground/20"
    >
      <span
        className="flex h-[30px] w-[30px] shrink-0 items-center justify-center overflow-hidden rounded-full text-[10px] font-medium"
        style={{ backgroundColor: stageTint(tint, TINT.pill), color: tint }}
      >
        {candidate.profile_pic_url ? (
          <img
            src={candidate.profile_pic_url}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          initials
        )}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-[13px] font-medium leading-none">
          {candidate.full_name || "Unknown"}
        </span>
        <span className="truncate text-[11px] leading-[1.2] text-muted-foreground">
          {[candidate.headline, candidate.experiences?.[0]?.company]
            .filter(Boolean)
            .join(" · ") || "No title"}
        </span>
      </span>
    </div>
  )
}
